import React, { useState } from 'react';
import styles from './NestedMultiSelect.module.scss';
import CheckboxInput from '../../../form/components/CheckboxInput';
import cx from 'classnames';
import { arraysAreEqual } from '../../../utils/array';
import { SearchInput } from '../SearchInput';
export const isOptionInSearchTerm = (option, searchTerm) => {
    var _a;
    return option.title.trim().toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
        ((_a = option.subTitle) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())) ||
        option.value.trim().toLowerCase().includes(searchTerm.trim().toLowerCase());
};
export const getSearchFilteredOptions = (options, searchTerm) => {
    return options
        .map((option) => (Object.assign(Object.assign({}, option), { subOptions: option.subOptions.filter((subOption) => isOptionInSearchTerm(subOption, searchTerm) || isOptionInSearchTerm(option, searchTerm)) })))
        .filter((option) => searchTerm
        ? isOptionInSearchTerm(option, searchTerm) || option.subOptions.length
        : option.subOptions.length);
};
export const isSelected = (option, selectedValues) => {
    if ('subOptions' in option && option.subOptions.length) {
        return option.subOptions.every((subOption) => selectedValues.includes(subOption.value));
    }
    return selectedValues.includes(option.value);
};
export const isPartialSelected = (option, selectedValues) => {
    if (option.subOptions.length) {
        return option.subOptions.some((subOption) => selectedValues.includes(subOption.value));
    }
    return false;
};
export const getUpdatedSelectedValues = (checked, option, selectedValues) => {
    if (checked) {
        if ('subOptions' in option && option.subOptions.length) {
            return Array.from(new Set([...selectedValues, ...option.subOptions.map(({ value }) => value)]));
        }
        return Array.from(new Set([...selectedValues, option.value]));
    }
    if ('subOptions' in option && option.subOptions.length) {
        return Array.from(new Set(selectedValues.filter((value) => !option.subOptions.some((subOption) => subOption.value === value))));
    }
    return Array.from(new Set(selectedValues.filter((value) => value !== option.value)));
};
export const NestedMultiSelect = ({ options = [], defaultValues = [], onChange, searchable = false, selectAllLabel, searchInputPlaceholder, className = '', handleAnalytics, analytics, }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedValues, setSelectedValues] = useState(defaultValues);
    const allOptionValues = options.flatMap((option) => { var _a, _b; return (_b = (_a = option.subOptions) === null || _a === void 0 ? void 0 : _a.map((subOption) => subOption.value)) !== null && _b !== void 0 ? _b : []; });
    const handleOnChange = (checked, option) => {
        setSelectedValues((currentValues) => {
            const updatedSelectedValues = getUpdatedSelectedValues(checked, option, currentValues);
            onChange(updatedSelectedValues);
            return updatedSelectedValues;
        });
        handleAnalytics === null || handleAnalytics === void 0 ? void 0 : handleAnalytics(checked, option.value);
    };
    const handleOnSelectAll = (checked) => {
        const updatedSelectedValues = checked ? allOptionValues : [];
        setSelectedValues(updatedSelectedValues);
        onChange(updatedSelectedValues);
        handleAnalytics === null || handleAnalytics === void 0 ? void 0 : handleAnalytics(checked, selectAllLabel || 'select all options');
    };
    if (!options.length)
        return null;
    const handleSearch = (inputValue) => {
        setSearchTerm(inputValue);
    };
    const searchFilteredOptions = getSearchFilteredOptions(options, searchTerm);
    return (<div className={cx(className, styles.nestedMultiSelect)}>
      {searchable && (<>
          <SearchInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} handleSearch={handleSearch} placeholder={searchInputPlaceholder} analytics={analytics}/>
          {!searchFilteredOptions.length && <p className={styles.noResults}>No results found</p>}
        </>)}
      {selectAllLabel && options.length === searchFilteredOptions.length ? (<li className={styles.option}>
          <CheckboxInput htmlLabel={<span className={styles.optionTitle}>{selectAllLabel}</span>} label={selectAllLabel} id={selectAllLabel} value={arraysAreEqual(selectedValues, allOptionValues)} onChange={(checked) => handleOnSelectAll(checked)} checkboxAlternative={(checked) => (<span className={styles.checkboxAlternative}>
                {checked ? 'Unselect' : 'Select all'}
              </span>)} verticalAlignment='center' disableHover/>
        </li>) : null}
      {searchFilteredOptions.map((option) => (<li className={styles.option} key={option.value}>
          <CheckboxInput htmlLabel={<span className={styles.optionTitle}>{option.title}</span>} label={option.title} id={option.value} value={isSelected(option, selectedValues)} isPartialChecked={isPartialSelected(option, selectedValues)} onChange={(checked) => handleOnChange(checked, option)} checkboxAlternative={(checked) => option.subOptions.length > 1 ? (<span className={styles.checkboxAlternative}>
                  {checked ? 'Unselect' : 'Select all'}
                </span>) : null} disabled={!(option.subOptions.length > 1)} verticalAlignment='center' disableHover/>
          {option.subOptions.length ? (<ul className={styles.subOptions}>
              {option.subOptions.map((subOption) => (<li className={styles.subOption} key={subOption.value}>
                  <CheckboxInput htmlLabel={<div className={styles.subOptionLabelContainer}>
                        <span className={styles.subOptionLabel}>
                          <span className={cx(styles.subOptionTitle, {
                            [styles.withoutSubTitle]: !subOption.subTitle,
                        })}>
                            {subOption.title}
                          </span>
                          <span className={styles.subOptionSubTitle}>{subOption.subTitle}</span>
                        </span>
                        <span className={styles.subOptionSubLabel}>{subOption.subLabel}</span>
                      </div>} label={subOption.title} id={subOption.value} value={isSelected(subOption, selectedValues)} onChange={(checked) => handleOnChange(checked, subOption)} verticalAlignment='center' disableHover/>
                </li>))}
            </ul>) : null}
        </li>))}
    </div>);
};
export default NestedMultiSelect;
